import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useFile } from "#src/hooks/useFile";
import { useClearRecordsCache } from "#src/queries/recordQueries";
import {
  Button,
  Dialog,
  FileUploadInput,
  Form,
  TextInput,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  RecordDomain,
  RecordValueType,
  RecordValueUpdateType,
} from "@validereinc/domain";
import React, { useEffect } from "react";

export const EditRecordValueDialog = ({
  isOpen,
  onClose,
  recordId,
  recordValue,
}: {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  recordValue: RecordValueType;
}) => {
  const form = useForm({});
  const { invalidate } = useClearRecordsCache();
  const { handleFileUpload, handleFileDownload } = useFile();
  const { toast } = useToast();
  const { getTypeName } = useMeasurementTypes();

  const measurementType = getTypeName(recordValue?.measurement_type);

  const handleSubmit = async () => {
    if (!recordId || !recordValue?.measurement_type) {
      return;
    }

    const formValues = form.getValues();
    const note = formValues?.note?.trim();
    const body = {
      measurement_unit: recordValue?.measurement_unit,
      configuration: {
        configuration_type: "manual",
        value: Number(formValues.record_value),
      },
      ...(note ? { note } : {}),
    } as RecordValueType;

    const file = formValues?.attachment?.[0];

    if (file) {
      body.attachment_filename = file.name;
      body.attachment_ref = file.ref;
    }

    try {
      await RecordDomain.recordValue.update(
        {
          recordId,
          measurementType: recordValue?.measurement_type,
        },
        body as RecordValueUpdateType
      );
      toast.push({
        intent: "success",
        description: "Success!",
      });
      onClose?.();
      invalidate();
    } catch (err) {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Unable to update record configuration",
      });
    }
  };

  useEffect(() => {
    form.reset();
  }, [isOpen]);

  return (
    <Dialog
      title="Edit Value"
      isOpen={isOpen}
      onClose={onClose}
      actionRow={[
        <Button
          onClick={form?.handleSubmit(handleSubmit)}
          variant="primary"
          disabled={form?.formState.isSubmitting}
          key="done-button"
        >
          Done
        </Button>,
      ]}
    >
      <Form {...form}>
        <TextInput
          name="record_value"
          label={measurementType}
          unit={recordValue?.measurement_unit}
          isRequired={true}
        />
        <TextInput
          name="note"
          label="Note"
          isRequired={false}
        />

        <FileUploadInput
          name="attachment"
          id="attachment"
          label="Attachment"
          placeholder="You can drag a file here to use as an attachment"
          isRequired={false}
          uploadFile={handleFileUpload}
          downloadFile={handleFileDownload}
        />
      </Form>
    </Dialog>
  );
};
